"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScheduledTaskToolResultSchema = exports.ScheduledTaskSchema = exports.NotificationSettingsSchema = exports.RepeatSpecSchema = exports.MonthlyOnSchema = exports.MonthWeekdaySchema = exports.RepeatKindSchema = exports.ScheduledTaskToolCallsSchema = exports.ScheduledTaskToolCallSchema = exports.SCHEDULED_TASK_TOOL_BLACKLIST = void 0;
exports.isToolSchedulable = isToolSchedulable;
var zod_1 = require("zod");
/**
 * Tool names that cannot be scheduled (chat-only or require live user/session context).
 */
exports.SCHEDULED_TASK_TOOL_BLACKLIST = [
    'transferAgent', // Agent switch; only meaningful in a conversation
    'approveWorkflowNode', // Requires user to approve in chat
    'clarifyUserRequest', // Asks user for clarification
    'clearResults', // Clears UI results panel; no session when scheduled
    'integrationSignInHelper', // Prompts user to sign in; requires user present
    'updateChatSummary', // Session housekeeping; which session when scheduled?
    'googleDriveFilePicker', // User must pick a file in UI
    'graphicsLoader', // Loads graphic for editing in session UI
    'createWorkflow', // Creates a workflow; requires user to select a template
    'updateWorkflow', // Updates a workflow; requires user to select a template
    'deleteWorkflow', // Deletes a workflow; requires user to confirm
    'preExecuteWorkflow',
    'rerunFromNodeExecution', // Reruns a node execution; requires user to select a node execution
    'getNodeExecution', // Gets a node execution; requires user to select a node
    'getToolsContext', // Gets the tools context; requires user to select a tool
    'getWorkflow', // Gets a workflow; requires user to select a workflow
    'getWorkflowExecution', // Gets a workflow execution; requires user to select a workflow execution
    'listWorkflows', // Lists workflows; requires user to select a workflow
    'listWorkflowExecutions', // Lists workflow executions; requires user to select a workflow execution
    'peekReference', // Peek reference; requires user to select a reference
    'listTags', // Lists tags; requires user to select a tag
    'scheduleTask', // Opens scheduling modal; requires user to approve
];
function isToolSchedulable(toolName) {
    return !exports.SCHEDULED_TASK_TOOL_BLACKLIST.includes(toolName);
}
/** Single tool call shape: { function: { name: string, arguments: string } }. */
exports.ScheduledTaskToolCallSchema = zod_1.z.object({
    function: zod_1.z.object({
        name: zod_1.z.string(),
        arguments: zod_1.z.union([zod_1.z.string(), zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())]),
    }),
});
/** Array of tool calls for batch execution. */
exports.ScheduledTaskToolCallsSchema = zod_1.z.array(exports.ScheduledTaskToolCallSchema);
/** Repeat kind for recurring tasks. */
exports.RepeatKindSchema = zod_1.z.enum(['daily', 'every_x_days', 'weekly', 'monthly']);
/** Nth weekday in month: 1=1st, 2=2nd, 3=3rd, 4=4th, 5=last. weekday 0–6 (Sun–Sat). */
exports.MonthWeekdaySchema = zod_1.z.object({
    which: zod_1.z.number().int().min(1).max(5),
    weekday: zod_1.z.number().int().min(0).max(6),
});
/** When to run in a month: specific day (1–31) or nth weekday (e.g. last Friday). */
exports.MonthlyOnSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal('dayOfMonth'), day: zod_1.z.number().int().min(1).max(31) }),
    zod_1.z.object({ type: zod_1.z.literal('nthWeekday'), monthWeekday: exports.MonthWeekdaySchema }),
]);
/**
 * Exact repeat specification for UI recovery and scheduling.
 * Single interval: meaning depends on kind (days / weeks / months).
 */
exports.RepeatSpecSchema = zod_1.z.object({
    kind: exports.RepeatKindSchema,
    /** UTC hour 0–23. */
    startHour: zod_1.z.number().int().min(0).max(23).optional(),
    /**
     * Repeat interval; unit depends on kind:
     * - every_x_days: N days
     * - weekly: N weeks (1 = every week)
     * - monthly: N months (1 = every month)
     */
    interval: zod_1.z.number().int().positive().optional(),
    /** For weekly: days of week 0–6 (Sun–Sat). */
    daysOfWeek: zod_1.z.array(zod_1.z.number().int().min(0).max(6)).optional(),
    /** For monthly: which day or nth weekday; mutually exclusive. */
    monthlyOn: exports.MonthlyOnSchema.optional(),
});
exports.NotificationSettingsSchema = zod_1.z.object({
    email: zod_1.z.object({ enabled: zod_1.z.boolean() }).optional().nullable(),
});
exports.ScheduledTaskSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    description: zod_1.z.string().optional(),
    toolCalls: exports.ScheduledTaskToolCallsSchema,
    timezone: zod_1.z.string().optional(),
    repeatSpec: exports.RepeatSpecSchema.optional().nullable(),
    maxRuns: zod_1.z.number().optional(),
    status: zod_1.z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED', 'PAUSED']),
    nextRunAt: zod_1.z.string().optional().nullable(),
    lastRunAt: zod_1.z.string().optional().nullable(),
    runCount: zod_1.z.number(),
    createdAt: zod_1.z.string(),
    bookmark: zod_1.z.boolean().default(false),
    notificationSettings: exports.NotificationSettingsSchema.optional().nullable(),
});
exports.ScheduledTaskToolResultSchema = zod_1.z.object({
    id: zod_1.z.string(),
    toolName: zod_1.z.string(),
    toolArguments: zod_1.z.record(zod_1.z.string(), zod_1.z.any()),
    status: zod_1.z.enum(['RUNNING', 'COMPLETED', 'FAILED']),
    result: zod_1.z.any().optional(),
    error: zod_1.z.string().optional(),
    startedAt: zod_1.z.string(),
    completedAt: zod_1.z.string().optional().nullable(),
    durationMs: zod_1.z.number().optional(),
    scheduledTaskId: zod_1.z.string(),
    createdAt: zod_1.z.string(),
});
